import { useState, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import type { TagListData } from "~/components/blocks/product/common/blocks/tag-list";
import { PRODUCT_TAB } from "~/components/blocks/product/common/video-season/video-season-tab/video-season-tab-contents/const";
import { sliceByTagNumber } from "~/components/blocks/product/common/video-season/video-season-tab/video-season-tab-contents/utils/slice-by-tag-number";
import { splitRangeLength } from "~/components/blocks/product/common/video-season/video-season-tab/video-season-tab-contents/utils/split-range-length";
import type { DownloadModalData } from "~/components/stores/detail-product/store";
import {
  isOpenDownloadModalVar,
  clickedContentIdVar,
  downloadModalDataVar,
} from "~/components/stores/detail-product/store";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useQueryString } from "~/hooks/use-query-string";
import { useGetVideoSpecialEpisodeLazyQuery } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";
import { getPlayDevice } from "~/util/get-device";

/** endCursorの数値から「-2」を引いたものをafterに指定するときの「-2」に該当する数値 */
const ALL_EPISODE_INDEX_NUMBER = 2;

type UseSpecialArgs = {
  /** 特典映像の合計数 */
  totalSpecialLength: number;
};

export const useSpecial = (args: UseSpecialArgs) => {
  const { totalSpecialLength } = args;

  const [videoSpecialEpisodeExec, { loading: isVideoSpecialEpisodeLoading, data: videoSpecialEpisodeData }] =
    useGetVideoSpecialEpisodeLazyQuery();

  const { isReady } = useRouter();
  const { isSpSize } = useMatchMedia();
  const { isLoggedIn } = useAuth();
  const { season, content } = useQueryString();

  const [activeIndex, setActiveIndex] = useState(0);

  const variables = {
    id: season,
    device: getPlayDevice(),
    isLoggedIn,
  };

  const episodeTagListArray: TagListData[] = useMemo(() => {
    return sliceByTagNumber({
      array: [...Array(totalSpecialLength)],
      splitNumber: splitRangeLength(totalSpecialLength),
    }).map((current, idx) => {
      const firstNumber = idx * splitRangeLength(totalSpecialLength) + 1;
      const lastNumber = firstNumber + current.length - 1;

      return {
        label: `${firstNumber} - ${lastNumber}`,
        onTagButtonClicked: async () => {
          if (!videoSpecialEpisodeData) return;

          const {
            video: {
              episodes: { allEpisodeNumbers },
            },
          } = videoSpecialEpisodeData;

          setActiveIndex(idx);

          try {
            void videoSpecialEpisodeExec({
              variables: {
                ...variables,
                /** 何件のデータを取得するか */
                first: lastNumber - firstNumber + 1,
                /**
                 * どのエピソードからデータを取得するか
                 * - MEMO : afterに「0」を指定するとデータ取得できない
                 * - MEMO : 1を指定すると2話から取得する仕様のため、1の場合は`undefined`を指定する。それ以外は`allEpisodeNumber[各タブの最初の数値 - 2]を指定する
                 */
                after: firstNumber === 1 ? undefined : allEpisodeNumbers[firstNumber - ALL_EPISODE_INDEX_NUMBER],
              },
            });
          } catch (e) {
            /* empty */
          }
        },
      };
    });
  }, [totalSpecialLength, !videoSpecialEpisodeData]);

  const itemHeight = useMemo(() => {
    return !isSpSize ? PRODUCT_TAB.PC_AND_TABLET.MIN_ITEM_HEIGHT : PRODUCT_TAB.SP.MIN_ITEM_HEIGHT;
  }, [isSpSize]);

  const imageWidth = useMemo(() => {
    return !isSpSize ? PRODUCT_TAB.PC_AND_TABLET.IMAGE_WIDTH : PRODUCT_TAB.SP.IMAGE_WIDTH;
  }, [isSpSize]);

  const imageHeight = useMemo(() => {
    return !isSpSize ? PRODUCT_TAB.PC_AND_TABLET.IMAGE_HEIGHT : PRODUCT_TAB.SP.IMAGE_HEIGHT;
  }, [isSpSize]);

  const episodeDownloadBtnClicked = (contentId: string, downloadModalData: DownloadModalData) => {
    isOpenDownloadModalVar(true);
    clickedContentIdVar(contentId);
    downloadModalDataVar(downloadModalData);
  };

  /** 初期描画時に特典映像タブに表示するための情報を取得する関数 */
  const effectSpecialEpisode = () => {
    if (!isReady || season === undefined) return;

    const controller = new AbortController();

    (async () => {
      try {
        const { data } = await videoSpecialEpisodeExec({
          variables: {
            ...variables,
            /** 初期描画時に何件のデータを取得するか */
            first: 1,
          },
        });
        if (!data) return;

        const {
          video: {
            episodes: { total, edges },
          },
        } = data;

        if (total === 0 || edges.length === 0) return;

        void videoSpecialEpisodeExec({
          variables: {
            ...variables,
            /** 初期描画時に何件のデータを取得するか */
            first: splitRangeLength(total),
          },
        });
      } catch (e) {
        /* empty */
      }
    })();

    return () => controller.abort();
  };

  useEffect(effectSpecialEpisode, [content, season, isReady]);

  const { edges: specialEpisodeEdges } = videoSpecialEpisodeData?.video.episodes ?? {};

  return {
    activeIndex,
    itemHeight,
    imageWidth,
    imageHeight,
    episodeTagListArray,
    isVideoSpecialEpisodeLoading,
    specialEpisodeEdges,
    episodeDownloadBtnClicked,
  };
};
